import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { TradeForm } from "@components/pages/Futures/components/Controls/TradeForm";
import { MobileTradeForm } from "@components/pages/Futures/components/Controls/MobileTradeForm";
import { usePlaceTrade } from "@components/pages/Futures/components/Controls/TradeForm/hooks/usePlaceTrade";
import {
  LEVERAGE_DEFAULT,
  LEVERAGE_MAX,
  LEVERAGE_MIN,
} from "@components/pages/Futures/components/Controls/TradeForm/constants";

export const Controls = ({ onSuccess, isMobile = false }) => {
  const { price: currentPrice, pair } = useSelector((state) => state.futures);
  const active_wallet = useSelector((state) => state.wallet.active_wallet);

  const [leverage, setLeverage] = useState(() => {
    const savedLeverage = localStorage.getItem("futuresLeverage");
    const parsedLeverage = Number(savedLeverage);

    return Number.isFinite(parsedLeverage) && parsedLeverage > 0
      ? parsedLeverage
      : LEVERAGE_DEFAULT;
  });

  const [longStats, setLongStats] = useState({
    max: 0,
    liqPrice: 0,
  });

  const [shortStats, setShortStats] = useState({
    max: 0,
    liqPrice: 0,
  });

  const [percentage, setPercentage] = useState(0);
  const [cost, setCost] = useState("");
  const [takeProfitPercent, setTakeProfitPercent] = useState(null);
  const [stopLossPercent, setStopLossPercent] = useState(null);
  const [direction, setDirection] = useState("long");

  const availableBalance = useMemo(() => {
    const raw = active_wallet?.balance;

    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }

    const parsed = parseFloat(String(raw ?? "").replace(",", "."));

    return Number.isFinite(parsed) ? parsed : 0;
  }, [active_wallet?.balance, active_wallet?.id]);

  const numericCost =
    cost === "" ? 0 : parseFloat(String(cost).replace(",", ".")) || 0;

  const commitWager = useCallback(
    (value) => {
      const numeric =
        typeof value === "number"
          ? value
          : parseFloat(String(value).replace(",", "."));
      const safe = Number.isFinite(numeric) ? numeric : 0;
      const rounded = Math.round(Math.max(safe, 0) * 100) / 100;
      let capped = 0;

      if (availableBalance > 0 && rounded <= availableBalance) {
        capped = rounded;
      } else if (availableBalance > 0) {
        let cents = Math.floor(availableBalance * 100 + 1e-8);

        if (cents / 100 > availableBalance) cents -= 1;

        capped = Math.max(cents, 0) / 100;
      }

      setCost(capped > 0 ? String(capped) : "");

      if (availableBalance > 0 && capped > 0) {
        setPercentage(Math.min((capped / availableBalance) * 100, 100));
      } else {
        setPercentage(0);
      }
    },
    [availableBalance],
  );

  const handleDirectionChange = (next) => {
    setDirection(next === "short" ? "short" : "long");
  };

  const handleLeverageChange = (next) => {
    const numeric = Math.trunc(Number(next));

    if (!Number.isFinite(numeric)) return;

    setLeverage(Math.min(LEVERAGE_MAX, Math.max(LEVERAGE_MIN, numeric)));
  };

  useEffect(() => {
    localStorage.setItem("futuresLeverage", String(leverage));
  }, [leverage]);

  useEffect(() => {
    const quantity =
      (availableBalance * (percentage / 100) * leverage) / currentPrice;

    const size = quantity * currentPrice;
    const initialMargin = size / leverage;

    const longLiqPrice = currentPrice * (1 - initialMargin / size);
    const shortLiqPrice = currentPrice * (1 + initialMargin / size);

    const max = availableBalance * leverage;

    setLongStats({
      max,
      liqPrice: longLiqPrice,
    });

    setShortStats({
      max,
      liqPrice: shortLiqPrice,
    });
  }, [availableBalance, currentPrice, leverage, percentage]);

  const { isPlacingTrade, handlePlaceTrade } = usePlaceTrade({
    currentPrice,
    pair,
    availableBalance,
    cost,
    numericCost,
    percentage,
    leverage,
    takeProfitPercent,
    stopLossPercent,
    direction,
    onSuccess,
  });

  const activeStats = direction === "short" ? shortStats : longStats;
  const Form = isMobile ? MobileTradeForm : TradeForm;

  return (
    <Form
      pair={pair}
      direction={direction}
      onDirectionChange={handleDirectionChange}
      leverage={leverage}
      onLeverageChange={handleLeverageChange}
      wager={numericCost}
      onWagerChange={commitWager}
      availableBalance={availableBalance}
      liquidationPrice={activeStats.liqPrice}
      entryPrice={currentPrice}
      setTakeProfitPercent={setTakeProfitPercent}
      setStopLossPercent={setStopLossPercent}
      onPlaceTrade={handlePlaceTrade}
      isPlacingTrade={isPlacingTrade}
    />
  );
};
